import React, { ReactNode } from 'react'
import { View } from 'react-native'
import { Block, BlockComponentProps } from '../../types'
import { space } from '../../tokens'
import { useDocletLayout } from '../../DocletLayoutContext'
import RichTextView from '../../richText/RichTextView'
import { isRichTextEmpty } from '../../richText/emptyContent'
import {
  DocletImage,
  DocletImageState,
  useDocletImage
} from '../image/ImageBlock'
import { imageTextLayout } from './layout'

/**
 * The image and text side by side (or the image above, on a narrow page).
 * Shared with the builder's editor, which passes its own image and text, and
 * its formatting row as `aboveText`: that goes above the whole row (so the
 * image moves down with the text) but lines up with the text column.
 * The image column is only as wide as the image, up to its share of the row,
 * so a small picture doesn't leave a gap before the text.
 */
export const ImageTextRow = ({
  block,
  image,
  imageNode,
  textNode,
  aboveText = null
}: {
  block: Block<'imageText'>
  image: DocletImageState
  imageNode: ReactNode
  textNode: ReactNode
  aboveText?: ReactNode
}) => {
  const { isWide } = useDocletLayout()
  const { stacked, imageShare, direction } = imageTextLayout(
    block.props,
    isWide
  )
  if (stacked) {
    return (
      <View>
        {aboveText}
        <View style={{ marginBottom: space[4] }}>{imageNode}</View>
        {textNode}
      </View>
    )
  }
  const gap = direction === 'row' ? 'marginRight' : 'marginLeft'
  const imageColumn = {
    width: image.size ? image.size.width : `${imageShare}%`,
    maxWidth: `${imageShare}%`,
    flexShrink: 0,
    [gap]: space[6]
  }
  return (
    <View>
      {!!aboveText && (
        <View style={{ flexDirection: direction }}>
          {/* Holds the image column's place */}
          <View style={imageColumn} />
          <View style={{ flex: 1, minWidth: 0 }}>{aboveText}</View>
        </View>
      )}
      <View style={{ flexDirection: direction, alignItems: 'flex-start' }}>
        <View style={{ ...imageColumn, marginBottom: space[4] }}>
          {imageNode}
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>{textNode}</View>
      </View>
    </View>
  )
}

// Legacy equivalent: `imagetext`. Without a (loadable) image it reads as a
// Text block; without text, as the image alone. Empty, it's left out.
const ImageTextBlock = ({ block }: BlockComponentProps<'imageText'>) => {
  const image = useDocletImage(block)
  const hasImage = !!image.uri && !image.failed
  const hasText = !isRichTextEmpty(block.props.body)
  if (!hasImage && !hasText) return null

  const text = hasText ? <RichTextView content={block.props.body} /> : null
  if (!hasImage) return text

  return (
    <ImageTextRow
      block={block}
      image={image}
      imageNode={<DocletImage block={block} image={image} />}
      textNode={text}
    />
  )
}

export default ImageTextBlock
